import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ILogo, ILogoState } from "Interfaces/interface.logos";
import toast from "react-hot-toast";
import logoService from "services/logosService";
import { extractErrorMessage } from "utils/ErrorHandlers";

const initialState: ILogoState = {
  logos: [],
  loading: false,
  error: null,
  isLogoLoaded: false,
  logoFetchError: null,
};

export const fetchUserLogos = createAsyncThunk<
  ILogo[],
  void,
  { rejectValue: string }
>("logo/fetchUserLogos", async (_, { rejectWithValue }) => {
  try {
    const response = await logoService.userLogos();
    return response;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message || "Failed to fetch logos");
    return rejectWithValue(message || "Failed to fetch logos");
  }
});

// export const saveUserLogo = createAsyncThunk<
//   { updatedLogos: ILogo[] },
//   File,
//   { rejectValue: string }
// >("logo/saveUserLogo", async (file, { rejectWithValue }) => {
//   try {
//     const response = await logoService.uploadLogo(file);
//     toast.success("Logo Uploaded Successfully");
//     return response;
//   } catch (error: any) {
//     return rejectWithValue(error.message || "Failed to upload logo");
//   }
// });

export const saveUserLogo = createAsyncThunk<
  { updatedLogos: ILogo[] },
  { file: File; name: string },
  { rejectValue: string }
>("logo/saveUserLogo", async (payload, { rejectWithValue }) => {
  try {
    const response = await logoService.uploadLogo(payload);
    toast.success("Logo uploaded successfully");
    return response;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message || "Failed to upload logo");
    return rejectWithValue(message || "Failed to upload logo");
  }
});

export const deleteUserLogos = createAsyncThunk<
  { updatedLogos: ILogo[] },
  string[],
  { rejectValue: string }
>("logo/deleteUserLogos", async (logoIds, { rejectWithValue }) => {
  try {
    const response = await logoService.deleteLogo(logoIds);
    toast.success("Logo deleted successfully");
    return response;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message || "Failed to delete logo");
    return rejectWithValue(message || "Failed to delete logo");
  }
});

const logoSlice = createSlice({
  name: "logo",
  initialState,
  reducers: {
    clearLogoState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserLogos.pending, (state) => {
        state.loading = true;
        state.logoFetchError = null;
      })
      .addCase(fetchUserLogos.fulfilled, (state, action) => {
        state.logos = action.payload;
        state.loading = false;
        state.isLogoLoaded = true;
      })
      .addCase(fetchUserLogos.rejected, (state, action) => {
        state.loading = false;
        state.logoFetchError = action.payload as string;
      })

      .addCase(saveUserLogo.pending, (state, action) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveUserLogo.fulfilled, (state, action) => {
        state.isLogoLoaded = false;
        state.logos = action.payload.updatedLogos;
        state.loading = false;
      })
      .addCase(saveUserLogo.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      .addCase(deleteUserLogos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUserLogos.fulfilled, (state, action) => {
        state.logos = action.payload.updatedLogos;
        state.loading = false;
        state.isLogoLoaded = false;
      })
      .addCase(deleteUserLogos.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });
  },
});

export const { clearLogoState } = logoSlice.actions;
export default logoSlice.reducer;

export const selectFetchedUserLogos = (state: any) => state.logo.logos;
export const selectLogoFetchError = (state: any) => state.logo.logoFetchError;
export const selectUserLogoLoading = (state: any) => state.logo.loading;
export const selectUserLogoError = (state: any) => state.logo.error;
export const selectUserLogoLoaded = (state: any) => state.logo.isLogoLoaded;
